import "./sessionExpired.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, GridContainer, GridItem, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { IconClockFill, IconSize } from "@amzn/stencil-react-icons";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

const SessionExpired: FC = () => {
  const location = useLocation();
  const history = useHistory();

  // Extract returnTo parameter from URL query string - memoized to avoid recalculation
  const returnTo = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get("returnTo") || "";
  }, [location.search]);

  // Memoize strings to avoid repeated getString calls
  const strings = useMemo(
    () => ({
      title: getString("sessionExpired.title"),
      description: getString("sessionExpired.description"),
      tryAgainButton: getString("sessionExpired.tryAgainButton"),
      returnHomeButton: getString("sessionExpired.returnHomeButton"),
    }),
    []
  );

  const onTryAgainClick = useCallback(() => {
    // Navigate back to the original page to refresh permissions
    history.push(returnTo);
  }, [history, returnTo]);

  const onReturnHomeClick = useCallback(() => {
    history.push("/");
  }, [history]);

  const renderHeader = useMemo(
    () => (
      <Row gridGap="S300">
        <IconClockFill color="orange70" size={IconSize.Large} title={strings.title} />
        <Text fontSize="T300" fontWeight="bold" data-cy="session-expired-title">
          {strings.title}
        </Text>
      </Row>
    ),
    [strings.title]
  );

  const renderContent = useMemo(
    () => (
      <Col gridGap="S300">
        <Text fontSize="T200" fontWeight="regular">
          {strings.description}
        </Text>
      </Col>
    ),
    [strings.description]
  );

  const renderButtons = useMemo(
    () => (
      <Row gridGap="S300">
        <Button data-testid="try-again-button" variant={ButtonVariant.Primary} onClick={onTryAgainClick}>
          {strings.tryAgainButton}
        </Button>
        <Button data-testid="return-home-button" variant={ButtonVariant.Tertiary} onClick={onReturnHomeClick}>
          {strings.returnHomeButton}
        </Button>
      </Row>
    ),
    [strings.tryAgainButton, strings.returnHomeButton, onTryAgainClick, onReturnHomeClick]
  );

  return (
    <PageMainContainer paddingTop="100px">
      <GridContainer>
        <GridItem lg={2} />
        <GridItem lg={2}>
          {/* Illustration */}
          <View display="flex" justifyContent="center" width="100%" height="100%">
            <div className="clock-icon" />
          </View>
        </GridItem>
        <GridItem lg={6}>
          <Col gridGap="S500">
            {/* Icon & title */}
            {renderHeader}

            {/* Description */}
            {renderContent}

            {/* Buttons */}
            {renderButtons}
          </Col>
        </GridItem>
        <GridItem lg={2} />
      </GridContainer>
    </PageMainContainer>
  );
};

export default SessionExpired;
